import React from 'react';
import { Button } from "@/components/ui/button";

/**
 * Componente de paginación reutilizable
 * @param {number} currentPage - Página actual (1-indexed)
 * @param {number} totalPages - Total de páginas
 * @param {function} onPageChange - Callback cuando cambia la página
 * @param {number} totalItems - Total de items (opcional, para mostrar info)
 * @param {number} itemsPerPage - Items por página (opcional, para mostrar info)
 */
function Pagination({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) {
  if (totalPages <= 1) return null;

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // Primera página
    if (startPage > 1) {
      pages.push(
        <Button
          key={1}
          size="sm"
          variant={currentPage === 1 ? "default" : "outline"}
          onClick={() => onPageChange(1)}
          className="min-w-[40px]"
        >
          1
        </Button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis-start" className="px-2">...</span>);
      }
    }
    
    // Páginas visibles
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          variant={currentPage === i ? "default" : "outline"}
          onClick={() => onPageChange(i)}
          className="min-w-[40px]"
        >
          {i}
        </Button>
      );
    }
    
    // Última página
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis-end" className="px-2">...</span>);
      }
      pages.push(
        <Button
          key={totalPages}
          size="sm"
          variant={currentPage === totalPages ? "default" : "outline"}
          onClick={() => onPageChange(totalPages)}
          className="min-w-[40px]"
        >
          {totalPages}
        </Button>
      );
    }
    
    return pages;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
      <div className="text-sm text-slate-600">
        {totalItems && itemsPerPage && (
          <>
            Mostrando <span className="font-medium">{startItem}</span> a{' '}
            <span className="font-medium">{endItem}</span> de{' '}
            <span className="font-medium">{totalItems}</span> resultados
          </>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Anterior
        </Button>
        
        {renderPageNumbers()}
        
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
}

export default Pagination;
